/**
 * Browser-side public archive: one compressed download with every visible
 * video, after which lists, filters, maps and search run locally.
 * Returns null on the server or if no archive exists (callers fall back to
 * the database).
 */
import type { VideoWithLocations } from "./queries";

export interface Archive {
  generatedAt: string;
  gzipBytes: number;
  videos: VideoWithLocations[]; // newest first
  byYoutuber: Map<string, VideoWithLocations[]>;
}

let cache: { at: number; p: Promise<Archive | null> } | null = null;
const TTL = 60_000;

export function loadArchive(): Promise<Archive | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (cache && Date.now() - cache.at < TTL) return cache.p;
  const prev = cache?.p;
  const p = fetchArchive().catch(async (e) => {
    console.warn("[archive] unavailable, using database", e);
    return (await prev?.catch(() => null)) ?? null;
  });
  cache = { at: Date.now(), p };
  return p;
}

let lastStamp = "";
let lastArchive: Archive | null = null;

async function fetchArchive(): Promise<Archive | null> {
  const res = await fetch("/api/public/archive", { cache: "no-cache" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`archive ${res.status}`);
  const compressed = await res.arrayBuffer();
  const gzipBytes = compressed.byteLength;
  let bytes = new Uint8Array(compressed);
  if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
    bytes = new Uint8Array(
      await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer(),
    );
  }
  const raw = JSON.parse(new TextDecoder().decode(bytes));
  if (raw.v !== 2) return null;
  if (raw.generated_at === lastStamp && lastArchive) return lastArchive;
  const ys = (raw.youtubers as any[]).map((y) => ({
    id: y[0] as string,
    obj: {
      handle: y[1], name: y[2], avatar_url: y[3], creator_id: y[4],
      creators: y[5] ? { slug: y[5] } : null,
    },
  }));
  const videos: VideoWithLocations[] = (raw.videos as any[]).map((r) => {
    const y = ys[r[2]];
    return {
      id: r[0], slug: r[1], youtuber_id: y.id, youtube_video_id: r[3], title: r[4],
      thumbnail_url:
        r[5] === 0 ? `https://i.ytimg.com/vi/${r[3]}/hqdefault.jpg`
        : r[5] === 1 ? `https://i.ytimg.com/vi/${r[3]}/maxresdefault.jpg` : r[5],
      published_at: r[6], duration_seconds: r[7], view_count: r[8], like_count: r[9],
      is_travel: !!r[10], hidden: false, geolocated: !!r[11],
      description: null, description_clean: null, transcript_summary: null,
      video_locations: (r[12] as any[]).map((l, i) => ({
        id: `${r[0]}:${i}`, name: l[0], country_code: l[1], country_name: l[2], region: l[3], city: l[4],
        latitude: l[5], longitude: l[6], is_primary: !!l[7], source: l[8],
      })),
      youtubers: y.obj,
    };
  });
  const byYoutuber = new Map<string, VideoWithLocations[]>();
  for (const v of videos) {
    const list = byYoutuber.get(v.youtuber_id) ?? [];
    list.push(v);
    byYoutuber.set(v.youtuber_id, list);
  }
  lastStamp = raw.generated_at;
  lastArchive = { generatedAt: raw.generated_at, gzipBytes, videos, byYoutuber };
  return lastArchive;
}

/** Use the archive when available, otherwise the database query. */
export async function viaArchive<T>(fromArchive: (a: Archive) => T, fallback: () => Promise<T>): Promise<T> {
  const a = await loadArchive();
  return a ? fromArchive(a) : fallback();
}

/** Accent/case-insensitive normalisation for instant search. */
export function norm(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}
